import React from "react";

function PolicyPageContentENG() {
  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Privacy Policy</h1>
      <p className="text-sm mb-6">Last updated 23/4/2024</p>
      <p className="mb-4">
        This privacy notice for KalimatKeywords ("we", "us", or "our"),
        describes how and why we might collect, store, use, and/or share
        ("process") your information when you use our services ("Services"),
        such as when you:
      </p>
      <ul className="list-disc list-inside mb-4">
        <li>
          Visit our website at kalimatkeywords.com, or any website of ours that
          links to this privacy notice.
        </li>
        <li>
          Engage with us in other related ways, including any sales, marketing,
          or events.
        </li>
      </ul>
      <p className="mb-16">
        Questions or concerns? Reading this privacy notice will help you
        understand your privacy rights and choices. If you do not agree with our
        policies and practices, please do not use our Services. If you still
        have any questions or concerns, please contact us at{" "}
        <a
          href="mailto:info@kalimatkeywords.com"
          className="text-custom-dark-blue font-bold hover:text-blue-800"
        >
          info@kalimatkeywords.com
        </a>
        .
      </p>

      {/* Second Section */}
      <h2 className="text-2xl font-semibold mb-2">
        1. WHAT INFORMATION DO WE COLLECT?
      </h2>
      <p className="mb-4">
        <strong>Personal information you disclose to us</strong>
        <br />
        In Short: We collect personal information that you provide to us.
      </p>
      <p className="mb-4">
        We collect personal information that you voluntarily provide to us when
        you express an interest in obtaining information about us or our
        products and Services, when you participate in activities on the
        Services, or otherwise when you contact us. The personal information
        that we collect depends on the context of your interactions with us and
        the Services, the choices you make, and the products and features you
        use. The personal information we collect may include the following:
      </p>
      <ul className="list-disc list-inside mb-4">
        <li>names</li>
        <li>email addresses</li>
        <li>mailing addresses</li>
      </ul>
      <p className="mb-4">
        <strong>Sensitive Information.</strong> We do not process sensitive
        information.
      </p>
      <p className="mb-4">
        <strong>Payment Data.</strong> We may collect data necessary to process
        your payment if you make purchases, such as your payment instrument
        number and the security code associated with your payment instrument.
        All payment data is stored by Paddle. You may find their privacy notice{" "}
        <a
          href="https://www.paddle.com/legal/privacy"
          className="text-custom-dark-blue font-bold hover:text-blue-800"
        >
          here
        </a>
        .
      </p>
      <p className="mb-4">
        <strong>Social Media Login Data.</strong> We may provide you with the
        option to register with us using your existing social media account
        details, like your Facebook, Twitter, or other social media account. If
        you choose to register in this way, we will collect the information
        described in the section called "HOW DO WE HANDLE YOUR SOCIAL LOGINS?"
        below.
      </p>
      <p className="mb-4">
        All personal information that you provide to us must be true, complete,
        and accurate, and you must notify us of any changes to such personal
        information.
      </p>

      {/* other Sections */}

      <h2 className="text-2xl font-semibold mb-2">
        2. HOW DO WE PROCESS YOUR INFORMATION?
      </h2>
      <p className="mb-4">
        In Short: We process your information to provide, improve, and
        administer our Services, communicate with you, for security and fraud
        prevention, and to comply with law. We may also process your information
        for other purposes with your consent.
      </p>
      <p className="mb-4">
        We process your personal information for a variety of reasons, depending
        on how you interact with our Services, including:
      </p>
      <ul className="list-disc list-inside mb-4">
        <li>
          To fulfill and manage your orders. We may process your information to
          fulfill and manage your orders, payments, returns, and exchanges made
          through the Services.
        </li>
        <li>
          To request feedback. We may process your information when necessary to
          request feedback and to contact you about your use of our Services.
        </li>
        <li>
          To deliver targeted advertising to you. We may process your
          information to develop and display personalized content and
          advertising tailored to your interests, location, and more.
        </li>
        <li>
          To post testimonials. We post testimonials on our Services that may
          contain personal information.
        </li>
      </ul>

      <h2 className="text-2xl font-semibold mt-6 mb-2">
        3. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
      </h2>
      <p className="mb-4">
        In Short: We may share information in specific situations described in
        this section and/or with the following third parties.
      </p>
      <p className="mb-4">
        We may need to share your personal information in the following
        situations:
        <ul className="list-disc list-inside mb-4">
          <li>
            Business Transfers. We may share or transfer your information in
            connection with, or during negotiations of, any merger, sale of
            company assets, financing, or acquisition of all or a portion of our
            business to another company.
          </li>
        </ul>
      </p>

      <h2 className="text-2xl font-semibold mt-6 mb-2">
        4. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
      </h2>
      <p className="mb-4">
        In Short: We may use cookies and other tracking technologies to collect
        and store your information.
      </p>
      <p className="mb-4">
        We may use cookies and similar tracking technologies (like web beacons
        and pixels) to access or store information. Specific information about
        how we use such technologies and how you can refuse certain cookies is
        set out in our Cookie Notice.
      </p>

      {/* other Sections */}
      <h2 className="text-2xl font-semibold mb-2">
        5. HOW DO WE HANDLE YOUR SOCIAL LOGINS?
      </h2>
      <p className="mb-4">
        In Short: If you choose to register or log in to our Services using a
        social media account, we may have access to certain information about
        you.
      </p>
      <p className="mb-4">
        Our Services offer you the ability to register and log in using your
        third-party social media account details (like your Facebook or Twitter
        logins). Where you choose to do this, we will receive certain profile
        information about you from your social media provider. The profile
        information we receive may vary depending on the social media provider
        concerned, but will often include your name, email address, friends
        list, and profile picture, as well as other information you choose to
        make public on such a social media platform.
      </p>
      <p className="mb-4">
        We will use the information we receive only for the purposes that are
        described in this privacy notice or that are otherwise made clear to you
        on the relevant Services. Please note that we do not control, and are
        not responsible for, other uses of your personal information by your
        third-party social media provider. We recommend that you review their
        privacy notice to understand how they collect, use, and share your
        personal information, and how you can set your privacy preferences on
        their sites and apps.
      </p>

      <h2 className="text-2xl font-semibold mt-6 mb-2">
        6. HOW LONG DO WE KEEP YOUR INFORMATION?
      </h2>
      <p className="mb-4">
        In Short: We keep your information for as long as necessary to fulfill
        the purposes outlined in this privacy notice unless otherwise required
        by law.
      </p>
      <p className="mb-4">
        We will only keep your personal information for as long as it is
        necessary for the purposes set out in this privacy notice, unless a
        longer retention period is required or permitted by law (such as tax,
        accounting, or other legal requirements). When we have no ongoing
        legitimate business need to process your personal information, we will
        either delete or anonymize such information, or, if this is not possible
        (for example, because your personal information has been stored in
        backup archives), then we will securely store your personal information
        and isolate it from any further processing until deletion is possible.
      </p>

      <h2 className="text-2xl font-semibold mt-6 mb-2">
        7. HOW DO WE KEEP YOUR INFORMATION SAFE?
      </h2>
      <p className="mb-4">
        In Short: We aim to protect your personal information through a system
        of organizational and technical security measures.
      </p>
      <p className="mb-4">
        We have implemented appropriate and reasonable technical and
        organizational security measures designed to protect the security of any
        personal information we process. However, despite our safeguards and
        efforts to secure your information, no electronic transmission over the
        Internet or information storage technology can be guaranteed to be 100%
        secure, so we cannot promise or guarantee that hackers, cybercriminals,
        or other unauthorized third parties will not be able to defeat our
        security and improperly collect, access, steal, or modify your
        information. Although we will do our best to protect your personal
        information, transmission of personal information to and from our
        Services is at your own risk. You should only access the Services within
        a secure environment.
      </p>

      {/* Section 8 9 19 */}
      <h2 className="text-2xl font-semibold mb-2">
        8. DO WE COLLECT INFORMATION FROM MINORS?
      </h2>
      <p className="mb-4">
        In Short: We do not knowingly collect data from or market to minors.
      </p>

      <h2 className="text-2xl font-semibold mt-6 mb-2">
        9. WHAT ARE YOUR PRIVACY RIGHTS?
      </h2>
      <p className="mb-4">
        In Short: You may review, change, or terminate your account at any time.
      </p>
      <p className="mb-4">
        Withdrawing your consent: If we are relying on your consent to process
        your personal information, which may be express and/or implied consent
        depending on the applicable law, you have the right to withdraw your
        consent at any time. You can withdraw your consent at any time by
        contacting us using the contact details provided in the section "HOW CAN
        YOU CONTACT US ABOUT THIS NOTICE?" below.
      </p>
      <p className="mb-4">
        However, please note that this will not affect the lawfulness of the
        processing before its withdrawal nor, when applicable law allows, will
        it affect the processing of your personal information conducted in
        reliance on lawful processing grounds other than consent.
      </p>
      <p className="mb-4">
        Cookies and similar technologies: Most web browsers are set to accept
        cookies by default. If you prefer, you can usually choose to set your
        browser to remove cookies and to reject cookies. If you choose to remove
        cookies or reject cookies, this could affect certain features or
        services of our Services.
      </p>
      <p className="mb-4">
        If you have questions or comments about your privacy rights, you may
        email us at{" "}
        <a
          href="mailto:info@kalimatkeywords.com"
          className="text-custom-dark-blue font-bold hover:text-blue-800"
        >
          info@kalimatkeywords.com
        </a>
        .
      </p>

      <h2 className="text-2xl font-semibold mt-6 mb-2">
        10. CONTROLS FOR DO-NOT-TRACK FEATURES
      </h2>
      <p className="mb-4">
        Most web browsers and some mobile operating systems and mobile
        applications include a Do-Not-Track ("DNT") feature or setting you can
        activate to signal your privacy preference not to have data about your
        online browsing activities monitored and collected. At this stage no
        uniform technology standard for recognizing and implementing DNT signals
        has been finalized. As such, we do not currently respond to DNT browser
        signals or any other mechanism that automatically communicates your
        choice not to be tracked online. If a standard for online tracking is
        adopted that we must follow in the future, we will inform you about that
        practice in a revised version of this privacy notice.
      </p>

      {/* Section 11 12 13 */}
      <h2 className="text-2xl font-semibold mb-2">
        11. DO WE MAKE UPDATES TO THIS NOTICE?
      </h2>
      <p className="mb-4">
        In Short: Yes, we will update this notice as necessary to stay compliant
        with relevant laws.
      </p>
      <p className="mb-4">
        We may update this privacy notice from time to time. The updated version
        will be indicated by an updated "Revised" date and the updated version
        will be effective as soon as it is accessible. If we make material
        changes to this privacy notice, we may notify you either by prominently
        posting a notice of such changes or by directly sending you a
        notification. We encourage you to review this privacy notice frequently
        to be informed of how we are protecting your information.
      </p>

      <h2 className="text-2xl font-semibold mt-6 mb-2">
        12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
      </h2>
      <p className="mb-4">
        If you have questions or comments about this notice, you may email us at{" "}
        <a
          href="mailto:info@kalimatkeywords.com"
          className="text-custom-dark-blue font-bold hover:text-blue-800"
        >
          info@kalimatkeywords.com
        </a>
        .
      </p>

      <h2 className="text-2xl font-semibold mt-6 mb-2">
        13. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
      </h2>
      <p className="mb-4">
        Based on the applicable laws of your country, you may have the right to
        request access to the personal information we collect from you, change
        that information, or delete it. To request to review, update, or delete
        your personal information, please fill out and submit a data subject
        access request.
      </p>
    </div>
  );
}

export default PolicyPageContentENG;
